import axios from 'axios';
import type { ApiResponse } from '../types/envelope.types';

/** An Error carrying the HTTP status (when there was a response). */
export interface ApiError extends Error {
  status?: number;
  correlationId?: string;
}

/**
 * Turns an envelope response into its `data`, throwing `Error(message)` when
 * `success` is false (or data is missing).
 */
export function unwrap<T>(res: { data: ApiResponse<T> }): T {
  const body = res.data;
  if (!body || body.success === false || body.data === null || body.data === undefined) {
    const err: ApiError = new Error(body?.message || 'Something went wrong');
    err.correlationId = body?.correlationId;
    throw err;
  }
  return body.data;
}

/**
 * Normalises any thrown value into an Error with the server's message
 * (`message`, or the first of `errors[]`) and the HTTP `status` attached.
 * Callers toast `err.message` as-is — the contract's 409 refusals are human
 * sentences by design.
 */
export function toApiError(err: unknown, fallback = 'Something went wrong'): ApiError {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as
      | { message?: string; errors?: string[] | null; correlationId?: string }
      | undefined;
    const serverMessage =
      body?.message || (Array.isArray(body?.errors) && body.errors[0]) || null;
    const out: ApiError = new Error(
      serverMessage ||
        (err.code === 'ECONNABORTED'
          ? 'The server took too long to answer'
          : !err.response
            ? 'No connection to the server'
            : fallback),
    );
    out.status = err.response?.status;
    out.correlationId = body?.correlationId;
    return out;
  }
  if (err instanceof Error) return err as ApiError;
  return new Error(fallback);
}

/** Runs a request, unwraps the envelope, and normalises failures. */
export async function call<T>(p: Promise<{ data: ApiResponse<T> }>, fallback?: string): Promise<T> {
  try {
    return unwrap<T>(await p);
  } catch (err) {
    throw toApiError(err, fallback);
  }
}
